import React, { useContext, useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Modal,
  ModalHeader,
  ModalBody,
  NavLink,
  Nav,
  NavItem,
  UncontrolledTooltip,
} from "reactstrap";
import AdminHeader from "components/Headers/AdminHeader";
import { CourseHeaderCard } from "./EnrollCard";

import { NotificationContext } from "components/CommonComponents/NotificationProvider";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { getCourseById } from "action/course";
import { urlDecodedId } from "components/CommonComponents/encodeDecodeFunction";
import EnrolledTraineesTab from "./EnrolledTraineesTab"; // adjust path as needed
import { courseUpcomingBatches } from "action/course";
import ReviewFeedback from "pages/ReviewFeedback/ReviewFeedback";

// Read More/Less Component for long text
const ReadMoreText = ({ text, maxLength = 300, className = "" }) => {
  const [expanded, setExpanded] = useState(false);

  if (!text) return null;

  const shouldTruncate = text.length > maxLength;
  const displayText =
    shouldTruncate && !expanded ? text.slice(0, maxLength) + "..." : text;

  return (
    <div className={className}>
      <div
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(displayText, {
            FORBID_TAGS: ["h1", "h2", "h3", "h4", "h5", "h6", "p", "br", "li"], // strip heading tags, keep their text
          }),
        }}
      />
      {shouldTruncate && (
        <button
          type="button"
          className="read-more-btn mt-1 read-more-button"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
        >
          {expanded ? "Read Less" : "Read More"}
          <i
            className={`read-more-button-icon fas fa-chevron-${expanded ? "up" : "down"} ml-1`}
          />
        </button>
      )}
    </div>
  );
};

const ChannelPartnerCourseDetails = () => {
  const navigate = useNavigate();
  const { notify } = useContext(NotificationContext);
  //store the api response
  const [courseDetails, setCourseDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [openLesson, setOpenLesson] = useState(null);
  const [isCertificateModalOpen, setIsCertificateModalOpen] = useState(false);
  const [modalScale, setModalScale] = useState(1);
  const modalObserverRef = React.useRef(null);
  const { id } = useParams();
  const courseId = id ? urlDecodedId(id) : null;
  const [activeTab, setActiveTab] = useState("details");
  const location = useLocation();
  const mode = location.state?.mode || "COURSE";

  // NEW: State for selected lesson language tab
  const [selectedLessonLanguage, setSelectedLessonLanguage] =
    useState("ENGLISH");
  const [upcomingBatches, setUpcomingBatches] = useState([]);
  const [expandedUpcomingBatch, setExpandedUpcomingBatch] = useState(null);

  //get the course details by id
  useEffect(() => {
    if (courseId) {
      fetchCourseDetails(courseId);
    }
  }, [courseId]);

  const fetchCourseDetails = async (courseId) => {
    try {
      setIsLoading(true);
      const [res, upcomingBatchesRes] = await Promise.all([
        getCourseById(courseId),
        courseUpcomingBatches(courseId),
      ]);

      if (res?.code === 200) {
        setCourseDetails(res.data);

        // Auto-select the first available language
        const englishLessons = res.data?.lessonByLanguage?.englishLessons || [];
        const hindiLessons = res.data?.lessonByLanguage?.hindiLessons || [];

        if (englishLessons.length > 0) {
          setSelectedLessonLanguage("ENGLISH");
        } else if (hindiLessons.length > 0) {
          setSelectedLessonLanguage("HINDI");
        }
      } else {
        const message = Array.isArray(res?.message)
          ? res.message.map((err) => err.Msg || JSON.stringify(err)).join(", ")
          : res?.message || "Failed to fetch course details.";

        notify("danger", message);
      }

      // Handle upcoming batches response
      if (upcomingBatchesRes?.code === 200) {
        setUpcomingBatches(upcomingBatchesRes.data || []);
      }
    } catch (error) {
      notify(
        "danger",
        error?.response?.data?.message || "Something went wrong.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  // NEW: Get lessons grouped by language from courseDetails
  const getLessonsByLanguage = () => {
    const englishLessons =
      courseDetails?.lessonByLanguage?.englishLessons || [];
    const hindiLessons = courseDetails?.lessonByLanguage?.hindiLessons || [];

    return {
      ENGLISH: englishLessons,
      HINDI: hindiLessons,
    };
  };

  // NEW: Get available languages
  const getAvailableLanguages = () => {
    const lessons = getLessonsByLanguage();
    const languages = [];

    if (lessons.ENGLISH.length > 0)
      languages.push({ key: "ENGLISH", label: "English" });
    if (lessons.HINDI.length > 0)
      languages.push({ key: "HINDI", label: "Hindi" });

    return languages;
  };

  // NEW: Get current lessons based on selected language
  const getCurrentLessons = () => {
    const lessons = getLessonsByLanguage();
    return lessons[selectedLessonLanguage] || [];
  };

  const currentLessons = getCurrentLessons();
  const availableLanguages = getAvailableLanguages();

  const toggleCertificateModal = () =>
    setIsCertificateModalOpen((prev) => !prev);

  const setModalRef = React.useCallback((node) => {
    if (modalObserverRef.current) {
      modalObserverRef.current.disconnect();
    }
    if (node) {
      const observer = new ResizeObserver((entries) => {
        for (let entry of entries) {
          if (entry.contentRect.width) {
            setModalScale(entry.contentRect.width / 900);
          }
        }
      });
      observer.observe(node);
      modalObserverRef.current = observer;
    }
  }, []);

  // const setPreviewRef = React.useCallback((node) => {
  //   if (previewObserverRef.current) {
  //     previewObserverRef.current.disconnect();
  //   }
  //   if (node) {
  //     const observer = new ResizeObserver((entries) => {
  //       for (let entry of entries) {
  //         if (entry.contentRect.width) {
  //           setPreviewScale(entry.contentRect.width / 900);
  //         }
  //       }
  //     });
  //     observer.observe(node);
  //     previewObserverRef.current = observer;
  //   }
  // }, []);

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      const params = new URLSearchParams(location.search);
      params.set("tab", tab);

      navigate(`${location.pathname}?${params.toString()}`, {
        replace: true,
        state: { mode },
      });
    }
  };

  // Format time from "10:00 AM" or "10:00" to "10:00 AM"
  const formatTime = (time) => {
    if (!time) return "-";
    try {
      // If time already has AM/PM, return as is
      if (time.includes("AM") || time.includes("PM")) {
        return time;
      }
      const [hours, minutes] = time.split(":");
      const h = parseInt(hours);
      const ampm = h >= 12 ? "PM" : "AM";
      const h12 = h % 12 || 12;
      return `${h12}:${minutes} ${ampm}`;
    } catch {
      return time;
    }
  };

  // Put this at module level — NOT inside CourseDetailsWeb
  const LessonContent = ({ isOpen, children }) => {
    const ref = React.useRef(null);
    const [maxHeight, setMaxHeight] = useState(0);

    useEffect(() => {
      if (!ref.current) return;

      const update = () => {
        setMaxHeight(isOpen ? ref.current.scrollHeight : 0);
      };

      update();

      const observer = new ResizeObserver(update);
      observer.observe(ref.current);
      return () => observer.disconnect();
    }, [isOpen]);

    return (
      <div
        className={`lesson-content ${isOpen ? "show" : ""}`}
        style={{ maxHeight: `${maxHeight}px` }}
      >
        <div ref={ref}>{children}</div>
      </div>
    );
  };

  return (
    <>
      <AdminHeader parentName="Courses" />
      <Container fluid className="mt--6">
        <Row>
          <Col md="12">
            {isLoading ? (
              <div className="loaderContainer mt-5">
                <i className="fa fa-spinner fa-spin fa-2xl loader" />
              </div>
            ) : !courseDetails ? (
              <div className="loaderContainer text-muted d-flex flex-column">
                <h4 className="text-muted mt-4">No course details available</h4>
              </div>
            ) : (
              <>
                <div
                  className="d-inline-flex align-items-center cursor text-white mb-3 back-btn"
                  onClick={() => {
                    if (window.history.length > 1) {
                      navigate(-1);
                    } else {
                      navigate("/channel-partner/home");
                    }
                  }}
                >
                  <i className="fa-solid fa-angle-left mr-2" />
                  <span className="font-weight-bold">Back</span>
                </div>
                {/* hero section */}
                <CourseHeaderCard
                  courseDetails={courseDetails}
                  isEnroll={true}
                  showEnrollButton={true}
                  showAddTraineeButton={true}
                />
                <Nav className="channelPartner-nav">
                  <NavItem className="channelPartner-nav-item">
                    <NavLink
                      className={`channelPartner-nav-link ${activeTab === "details" ? "active" : ""}`}
                      onClick={() => toggleTab("details")}
                    >
                      <i className="fa fa-book mr-2" />
                      Course Details
                    </NavLink>
                  </NavItem>

                  <NavItem className="channelPartner-nav-item">
                    <NavLink
                      className={`channelPartner-nav-link ${activeTab === "trainees" ? "active" : ""}`}
                      onClick={() => toggleTab("trainees")}
                    >
                      <i className="fa fa-users mr-2" />
                      Trainees
                    </NavLink>
                  </NavItem>

                  <NavItem className="channelPartner-nav-item">
                    <NavLink
                      className={`channelPartner-nav-link ${
                        activeTab === "review-feedback" ? "active" : ""
                      }`}
                      onClick={() => toggleTab("review-feedback")}
                    >
                      <i className="fa fa-star mr-2" />
                      Reviews
                    </NavLink>
                  </NavItem>
                </Nav>
                {activeTab === "details" ? (
                  <Row className="mt-4">
                    <Col lg="8">
                      {/* about section */}
                      <Card className="content-card">
                        <CardBody>
                          <h3 className="channelPartner-section-title">
                            {courseDetails?.courseName || "-"}
                          </h3>
                          <ReadMoreText
                            text={courseDetails.courseDescription}
                            maxLength={200}
                            className="channelPartner-desc-text text-justify"
                          />
                        </CardBody>
                      </Card>

                      {courseDetails?.coursePrerequisiteDocument?.length >
                        0 && (
                        <Card className="content-card">
                          <CardBody>
                            <h3 className="channelPartner-section-title">
                              Training Prerequisites Documents
                            </h3>

                            <ul className="list-dot channelPartner-desc-text">
                              {courseDetails.coursePrerequisiteDocument.map(
                                (doc) => (
                                  <li key={doc.ID}>{doc.courseDocumentName}</li>
                                ),
                              )}
                            </ul>
                          </CardBody>
                        </Card>
                      )}
                      {/* <Card className="content-card">
                        <CardBody>
                          <h3 className="channelPartner-section-title">
                            Certificate
                          </h3>
                          <CardBody>
                            <div className="certificate-side-preview">
                              {courseDetails?.certificateTemplates
                                ?.certificateTemplate ? (
                                <div className="channelPartner-desc-text text-justify">
                                  Enrolled Trainees will receive certificate
                                  once the course is completed.
                                </div>
                              ) : (
                                <div className="text-center">
                                  <p className="text-muted mb-0">
                                    Certificate is not enabled for this course.
                                  </p>
                                </div>
                              )}
                            </div>
                          </CardBody>
                        </CardBody>
                      </Card> */}
                    </Col>
                    <Col lg="4">
                      {/* training content section - UPDATED with language tabs */}
                      <Card className="content-card">
                        <CardBody>
                          <div className="section-header-i">
                            <div className="section-marker"></div>
                            <h4 className="doc-title">Lessons</h4>
                            <i
                              className="fa fa-info-circle info-icon"
                              id="info-lessons"
                            ></i>
                            <UncontrolledTooltip
                              target="info-lessons"
                              placement="right"
                            >
                              Course lessons by language
                            </UncontrolledTooltip>
                          </div>

                          {/* Language Tabs */}
                          {availableLanguages.length > 1 && (
                            <div className="cp-lesson-language-tabs">
                              {availableLanguages.map((lang) => (
                                <button
                                  key={lang.key}
                                  className={`language-tab-btn ${
                                    selectedLessonLanguage === lang.key
                                      ? "active"
                                      : ""
                                  }`}
                                  onClick={() => {
                                    setSelectedLessonLanguage(lang.key);
                                    setOpenLesson(null); // Close any open lesson when switching tabs
                                  }}
                                >
                                  <span className="tab-icon">
                                    <i
                                      className={`fa-solid ${lang.key === "ENGLISH" ? "fa-e" : "fa-h"}`}
                                    />
                                  </span>
                                  <span>{lang.label}</span>
                                  <span className="lesson-count">
                                    ({getLessonsByLanguage()[lang.key].length})
                                  </span>
                                </button>
                              ))}
                            </div>
                          )}

                          {/* Lessons List */}
                          {currentLessons.length > 0 &&
                            currentLessons.map((lesson) => {
                              const isOpen = openLesson === lesson.id;

                              return (
                                <div className="day-block" key={lesson.id}>
                                  <div
                                    className="lesson-header"
                                    onClick={() =>
                                      setOpenLesson(isOpen ? null : lesson.id)
                                    }
                                  >
                                    <div>
                                      <strong>
                                        {lesson.lesson_seq_no}.{" "}
                                        {lesson.lesson_name}
                                      </strong>
                                    </div>

                                    <div className="lesson-arrow">
                                      <i
                                        className={`fas fa-chevron-${
                                          isOpen ? "up" : "down"
                                        }`}
                                      />
                                    </div>
                                  </div>

                                  <LessonContent isOpen={isOpen}>
                                    <ReadMoreText
                                      text={
                                        lesson?.description ||
                                        "<p>No description available.</p>"
                                      }
                                      maxLength={300}
                                      className="desc-text text-justify"
                                    />
                                  </LessonContent>
                                </div>
                              );
                            })}

                          {/* Show message if no lessons at all */}
                          {availableLanguages.length === 0 && (
                            <p className="text-muted text-center py-3">
                              <i className="fa-circle-info mr-2"></i>
                              No lessons available for this course.
                            </p>
                          )}
                        </CardBody>
                      </Card>
                      {/* Upcoming Batches Card - Below Lessons Card (Similar to EnrollPage Payment Summary) */}
                      {upcomingBatches.length > 0 && (
                        <div className="upcoming-batches-wrapper">
                          <Card className="card-enroll">
                            <CardBody>
                              <div className="section-header-i">
                                <div className="section-marker"></div>
                                <h4 className="doc-title">Upcoming Batches</h4>
                                <i
                                  className="fa fa-info-circle info-icon"
                                  id="info-upcoming"
                                ></i>
                                <UncontrolledTooltip
                                  target="info-upcoming"
                                  placement="right"
                                >
                                  Upcoming batches for this course
                                </UncontrolledTooltip>
                              </div>

                              <div className="upcoming-batches-list">
                                {upcomingBatches.map((batch) => (
                                  <div
                                    key={batch.ID}
                                    className="upcoming-batch-card"
                                  >
                                    <div
                                      className="upcoming-batch-header"
                                      onClick={() =>
                                        setExpandedUpcomingBatch(
                                          expandedUpcomingBatch === batch.ID
                                            ? null
                                            : batch.ID,
                                        )
                                      }
                                    >
                                      <div className="upcoming-batch-left">
                                        <span className="upcoming-batch-name">
                                          {batch.name}
                                        </span>
                                        <span className="upcoming-batch-session">
                                          <i className="fa fa-clock"></i>
                                          {batch.batchSession ||
                                            "Session"} •{" "}
                                          {formatTime(batch.startTime)} -{" "}
                                          {formatTime(batch.endTime)}
                                        </span>
                                      </div>
                                      <div className="upcoming-batch-right">
                                        <span className="lesson-arrow">
                                          <i
                                            className={`ni ${
                                              expandedUpcomingBatch === batch.ID
                                                ? "ni-bold-up"
                                                : "ni-bold-down"
                                            }`}
                                          />
                                        </span>
                                        <span className="upcoming-batch-status open">
                                          <i className="fa fa-circle"></i> Open
                                        </span>
                                      </div>
                                    </div>

                                    {expandedUpcomingBatch === batch.ID && (
                                      <div className="upcoming-batch-details">
                                        {/* Registration */}
                                        <div className="upcoming-batch-row">
                                          <span className="upcoming-batch-label">
                                            <i className="fa fa-calendar"></i>{" "}
                                            Registration
                                          </span>
                                          <span className="upcoming-batch-value">
                                            {batch.registrationStartDate} -{" "}
                                            {batch.registrationEndDate}
                                          </span>
                                        </div>

                                        {/* Batch Dates */}
                                        <div className="upcoming-batch-row">
                                          <span className="upcoming-batch-label">
                                            <i className="fa fa-calendar-check"></i>{" "}
                                            Batch Dates
                                          </span>
                                          <span className="upcoming-batch-value">
                                            {batch.batchStartDate} -{" "}
                                            {batch.batchEndDate}
                                          </span>
                                        </div>

                                        {/* Duration */}
                                        <div className="upcoming-batch-row">
                                          <span className="upcoming-batch-label">
                                            <i className="fa fa-clock"></i>{" "}
                                            Duration
                                          </span>
                                          <span className="upcoming-batch-value">
                                            {batch.duration || "-"}
                                          </span>
                                        </div>

                                        {/* Location */}
                                        <div className="upcoming-batch-row">
                                          <span className="upcoming-batch-label">
                                            <i className="fa fa-map-marker"></i>{" "}
                                            Location
                                          </span>
                                          <span className="upcoming-batch-value">
                                            {batch.trainingCenters?.name || "-"}
                                          </span>
                                        </div>

                                        {/* Language */}
                                        {batch.batchLanguage && (
                                          <div className="upcoming-batch-row">
                                            <span className="upcoming-batch-label">
                                              <i className="fa fa-language"></i>{" "}
                                              Language
                                            </span>
                                            <span className="upcoming-batch-value">
                                              {batch.batchLanguage}
                                            </span>
                                          </div>
                                        )}

                                        {/* Available Seats */}
                                        {batch.availableSeats &&
                                          batch.availableSeats !== "NA" && (
                                            <div className="upcoming-batch-row">
                                              <span className="upcoming-batch-label">
                                                <i className="fa fa-users"></i>{" "}
                                                Available Seats
                                              </span>
                                              <span className="upcoming-batch-value">
                                                {batch.availableSeats}
                                              </span>
                                            </div>
                                          )}

                                        {/* Description - Now properly aligned with label and content side by side */}
                                        {batch.description && (
                                          <div className="upcoming-batch-row upcoming-batch-row-description">
                                            <span className="upcoming-batch-label">
                                              <i className="fa fa-info-circle"></i>{" "}
                                              Description
                                            </span>
                                            <span className="upcoming-batch-value">
                                              <ReadMoreText
                                                text={batch.description}
                                                maxLength={100}
                                                className="channelPartner-desc-text text-justify"
                                              />
                                            </span>
                                          </div>
                                        )}

                                        {/* Schedule Info */}
                                        {batch.scheduleInfo && (
                                          <div className="upcoming-batch-row upcoming-batch-row-description">
                                            <span className="upcoming-batch-label">
                                              <i className="fa fa-calendar-alt"></i>{" "}
                                              Schedule
                                            </span>
                                            <span className="upcoming-batch-value">
                                              <ReadMoreText
                                                text={batch.scheduleInfo}
                                                maxLength={100}
                                                className="channelPartner-desc-text text-justify"
                                              />
                                            </span>
                                          </div>
                                        )}

                                        {/* Additional Info */}
                                        {batch.additionalInfo && (
                                          <div className="upcoming-batch-row upcoming-batch-row-description">
                                            <span className="upcoming-batch-label">
                                              <i className="fa fa-info-circle"></i>{" "}
                                              Additional Info
                                            </span>
                                            <span className="upcoming-batch-value">
                                              <ReadMoreText
                                                text={batch.additionalInfo}
                                                maxLength={100}
                                                className="channelPartner-desc-text text-justify"
                                              />
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </CardBody>
                          </Card>
                        </div>
                      )}
                    </Col>
                  </Row>
                ) : activeTab === "trainees" ? (
                  <Row className="mt-4">
                    <Col md="12">
                      <EnrolledTraineesTab courseId={courseId} />
                    </Col>
                  </Row>
                ) : activeTab === "review-feedback" ? (
                  <ReviewFeedback courseId={courseId} isChannelPartner={true}/>
                ) : null}
              </>
            )}
          </Col>
        </Row>
      </Container>
      <Modal
        isOpen={isCertificateModalOpen}
        toggle={toggleCertificateModal}
        size="xl"
        centered
        className="certificate-preview-modal"
      >
        <ModalHeader toggle={toggleCertificateModal}>
          Certificate Preview
        </ModalHeader>

        <ModalBody className="certificate-preview-modal-body">
          {courseDetails?.certificateTemplates?.certificateTemplate ? (
            (() => {
              try {
                const templateJson = JSON.parse(
                  courseDetails?.certificateTemplates.certificateTemplate,
                );
                return (
                  <div
                    className="certificate-preview-container mx-auto"
                    ref={setModalRef}
                  >
                    <div
                      className="course-details-certificate-preview-canvas"
                      style={{
                        transform: `scale(${modalScale})`,
                        backgroundImage: `url(${templateJson.previewImage || "/certificate-bg.png"})`,
                      }}
                    >
                      {templateJson.layout?.map((item, index) => {
                        if (item.type === "text") {
                          return (
                            <div
                              key={index}
                              style={{
                                position: "absolute",
                                left: `${item.x}%`,
                                top: `${item.y}%`,
                                fontSize: `${item.fontSize || 24}px`,
                                fontFamily: item.fontFamily || "Arial",
                                color: item.color || "#000",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {item.text}
                            </div>
                          );
                        }

                        if (
                          item.type === "signature" ||
                          item.type === "image"
                        ) {
                          return (
                            <img
                              key={index}
                              src={item.src}
                              alt="signature"
                              style={{
                                position: "absolute",
                                left: `${item.x}%`,
                                top: `${item.y}%`,
                                width: `${(item.scaleX || 0.5) * 32}%`,
                                height: "auto",
                                objectFit: "contain",
                              }}
                            />
                          );
                        }

                        if (
                          item.type === "shape" &&
                          item.shapeType === "line"
                        ) {
                          return (
                            <div
                              key={index}
                              style={{
                                position: "absolute",
                                left: `${item.x}%`,
                                top: `${item.y}%`,
                                width: `${((item.x2 - item.x1) / 900) * 100}%`,
                                height: `${item.strokeWidth || 2}px`,
                                backgroundColor: item.stroke || "#000",
                              }}
                            />
                          );
                        }

                        return null;
                      })}
                    </div>
                  </div>
                );
              } catch (err) {
                return (
                  <div className="text-center text-danger">
                    Error rendering certificate template.
                  </div>
                );
              }
            })()
          ) : (
            <div className="text-center p-5 text-muted">
              No certificate added.
            </div>
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

export default ChannelPartnerCourseDetails;
